import { Fragment } from 'react';
import { Svg } from '@react-pdf/renderer';
import floorPlanElements from './floorPlanElements';

/**
* @function FloorPlan
* @description Renders a complete floor plan in a PDF document by composing
* multiple floor plan elements in a defined order.
* 
* @param {Object} props
* @param {Object} props.bbox - Bounding box dimensions for SVG container
* @param {number} props.bbox.width - Width of floor plan
* @param {number} props.bbox.height - Height of floor plan
* @param {Object} props.constants - Configuration constants
* @param {Array<string>} props.constants.reportPlotElements - Ordered list of elements to render
* 
* @description
* Orchestrates floor plan rendering by:
* 1. Creating SVG container sized to bounding box
* 2. Mapping through reportPlotElements array
* 3. Rendering each element in sequence using floorPlanElements components
* 4. Passing through props to all child elements
* 
* @example
* <FloorPlan 
*   bbox={{width: 600, height: 400}}
*   constants={{
*     reportPlotElements: [
*       'Partitions',
*       'Storage',
*       'LoadingDocks',
*       'Devices'
*     ]
*   }}
* />
* 
* @note
* - Uses @react-pdf/renderer for PDF generation
* - Elements render in order specified by reportPlotElements
* - Each element receives same props for consistency
* - SVG container ensures proper scaling and positioning
* - Supports all floor plan components from floorPlanElements
*/
const FloorPlan = (props) => {
  const { bbox, constants: { reportPlotElements } } = props;
  return (
    <Fragment>
      <Svg width={bbox.width} height={bbox.height} >
        {reportPlotElements.map((element, index) => {
          const Element = floorPlanElements[element];
          return (
            <Element key={`${element}-${index}`} {...props}  />
          );
        })
        }
      </Svg>
    </Fragment>
  )
};

export default FloorPlan;